import { Component, h, Prop, Host, Event, EventEmitter, Element, Watch, Method } from "@stencil/core";

@Component({
	tag: "smart-checkbox",
	styleUrl: "./smart-checkbox.scss",
	shadow: true,
})

export class SmartCheckbox {
	@Element() el: HTMLSmartCheckboxElement;
	private nativeInput?: HTMLInputElement;
	private hiddenInput: HTMLInputElement;

	@Prop() identifier?: string;
	@Prop() label!: string;
	@Prop() name!: string;
	@Prop() value!: string;

	@Prop({ mutable: true, reflect: true }) checked?: boolean = false;
	@Prop({ reflect: false }) required?: boolean = false;
	@Prop({ reflect: false }) disabled?: boolean = false;

	/** form#id - Associates the control with a form element. */
	@Prop() form?: string;

	@Prop() theme: "primary" | null = "primary";

	@Event() smartChange: EventEmitter<boolean>;

	@Watch("checked")
	updateHiddenInput() {
		if(this.hiddenInput) {
			this.hiddenInput.checked = this.checked;
			this.hiddenInput.value = this.value;
		}
	}

	/** Sets focus on the radio button. */
	@Method()
	async focusInner() {
		if (this.nativeInput !== undefined) {
			this.nativeInput.focus();
		}
	}

	private handleKeyDown = (ev: KeyboardEvent) => {
		if(ev.key === " ") {
			this.el.dispatchEvent(new InputEvent("click", { bubbles: true }));
		}
	};

	private handleClick = (ev?: MouseEvent) => {
		if(this.disabled) {
			ev.preventDefault();
			return;
		}
		this.checked = !this.checked;

		if (!ev.isTrusted) {
			this.el.dispatchEvent(new InputEvent("change", { bubbles: true }));
			this.smartChange.emit(this.checked);
		}
	};

	private handleChange = (event: InputEvent) => {
		this.el.dispatchEvent(new InputEvent("change", event));
		this.smartChange.emit(this.checked);
	};

	private createHiddenInput() {
		this.hiddenInput = document.createElement("input");
		this.hiddenInput.type = "checkbox";
		this.hiddenInput.checked = false;
		this.hiddenInput.style.visibility = "hidden";
		this.hiddenInput.style.position = "absolute";
		this.hiddenInput.style.pointerEvents = "none";
		this.el.appendChild(this.hiddenInput);
		this.hiddenInput.setAttribute("form", this.form);
	}

	componentWillLoad() {
		if (this.form && this.name) {
			this.createHiddenInput();
			this.hiddenInput.checked = this.checked;
			this.hiddenInput.name = this.name;

			if(this.identifier){
				this.hiddenInput.id = this.identifier;
			}

			if (this.value) {
				this.hiddenInput.value = this.value;
			}
		}
	}

	render() {
		const randomId: string = (this.name ? this.name : "cid") + new Date().getTime();
		const customId = this.identifier ?? randomId;

		return (
			<Host
				role="checkbox"
				aria-checked={this.checked.toString()}
				aria-labelledby={`label-${customId}`}
				tabindex="0"
				onKeyPress={this.handleKeyDown}
				onClick={this.handleClick}
				class={{
					"wrapper": true,
					[this.theme]: true,
					"checked": this.checked,
					"disabled": this.disabled,
				}}
			>
				<input 
					type="checkbox"
					id={customId}
					name={this.name}
					value={this.value}
					checked={this.checked}
					disabled={this.disabled}
					required={this.required}
					ref={ref => this.nativeInput = ref}
					onChange={this.handleChange}
					tabindex="-1"
				/>
				<smart-text id={`label-${customId}`} as="label" variant="subtitle-2">
					{this.label}
				</smart-text>
			</Host>
		);
	}
}
